// DateRangeInline.jsx
import { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { getISTDate } from "../../utility/getISTDate";

const fmt = (d) => {
  if (!d) return "Select";
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

export default function DateRangeInline({ fromDate, toDate, onChange }) {
  const [internalFrom, setInternalFrom] = useState(fromDate || getISTDate());
  // const [internalTo, setInternalTo] = useState(
  //   toDate || new Date(new Date().setDate(new Date().getDate() + 10)),
  // );
  const [internalTo, setInternalTo] = useState(() => {
    if (toDate) return toDate;
    const d = getISTDate(); // ✅
    d.setDate(d.getDate() + 10);
    return d;
  });
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (fromDate) setInternalFrom(fromDate);
    if (toDate) setInternalTo(toDate);
  }, [fromDate, toDate]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleChange = (dates) => {
    const [start, end] = dates;
    if (start) setInternalFrom(start);
    setInternalTo(end || null);
    if (start && end) onChange?.(start, end);
  };

  const shiftFrom = (days) => {
    const d = new Date(internalFrom);
    d.setDate(d.getDate() + days);

    const today = getISTDate();
    // const today = new Date();
    today.setHours(0, 0, 0, 0);

    // ✅ Allow going back to today but not before today
    if (d >= today) {
      // If new from date is after to date, also push to date forward
      if (internalTo && d >= internalTo) {
        const newTo = new Date(d);
        newTo.setDate(newTo.getDate() + 1);
        setInternalTo(newTo);
        onChange?.(d, newTo);
      } else {
        onChange?.(d, internalTo);
      }
      setInternalFrom(d);
    }
  };

  const shiftTo = (days) => {
    const d = new Date(internalTo);
    d.setDate(d.getDate() + days);
    if (d > internalFrom) {
      setInternalTo(d);
      onChange?.(internalFrom, d);
    }
  };

  return (
    <>
      <style>{`
        .dri-dp .react-datepicker { background: transparent !important; border: none !important; font-family: inherit !important; }
        .dri-dp .react-datepicker__month-container { padding: 0 8px; }
        .dri-dp .react-datepicker__header { background: transparent !important; border-bottom: 1px solid #334155 !important; padding-bottom: 10px !important; }
        .dri-dp .react-datepicker__current-month { color: #f1f5f9 !important; font-size: 14px !important; font-weight: 700 !important; margin-bottom: 8px !important; }
        .dri-dp .react-datepicker__day-name { color: #64748b !important; font-size: 10px !important; font-weight: 600 !important; width: 32px !important; text-transform: uppercase; }
        .dri-dp .react-datepicker__day { color: #cbd5e1 !important; width: 32px !important; line-height: 32px !important; border-radius: 50% !important; font-size: 12px !important; margin: 1px !important; transition: all 0.15s !important; }
        .dri-dp .react-datepicker__day:hover { background: #0095da44 !important; color: #fff !important; }
        .dri-dp .react-datepicker__day--selected,
        .dri-dp .react-datepicker__day--range-start,
        .dri-dp .react-datepicker__day--range-end { background: #0095da !important; color: #fff !important; font-weight: 700 !important; }
        .dri-dp .react-datepicker__day--in-range { background: #0095da33 !important; color: #e2e8f0 !important; border-radius: 0 !important; }
        .dri-dp .react-datepicker__day--range-start { border-radius: 50% 0 0 50% !important; }
        .dri-dp .react-datepicker__day--range-end { border-radius: 0 50% 50% 0 !important; }
        .dri-dp .react-datepicker__day--disabled { color: #2d3f55 !important; cursor: not-allowed !important; }
        .dri-dp .react-datepicker__day--outside-month { color: #2d3f55 !important; }
        .dri-dp .react-datepicker__navigation-icon::before { border-color: #64748b !important; }
        .dri-dp .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before { border-color: #fff !important; }
        .dri-dp .react-datepicker__navigation { top: 6px !important; }
        .dri-dp .react-datepicker__day--in-selecting-range { background: #0095da33 !important; border-radius: 0 !important; }
        .dri-dp .react-datepicker__day--today { font-weight: 700 !important; text-decoration: underline !important; }
      `}</style>

      <div ref={ref} style={{ position: "relative" }}>
        <div
          onClick={() => setOpen(!open)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "6px 8px",
            cursor: "pointer",
            minWidth: "260px",
            userSelect: "none",
            height: "46px",
          }}
        >
          {/* From block */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              shiftFrom(-1);
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "2px 4px",
              color: "#94a3b8",
            }}
          >
            <FaChevronLeft style={{ fontSize: "10px" }} />
          </button>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flex: 1,
            }}
          >
            <FaCalendarAlt
              style={{ color: "#0095da", fontSize: "13px", flexShrink: 0 }}
            />
            <span
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#0f172a",
                whiteSpace: "nowrap",
              }}
            >
              {fmt(internalFrom)}
            </span>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              shiftFrom(1);
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "2px 4px",
              color: "#94a3b8",
            }}
          >
            <FaChevronRight style={{ fontSize: "10px" }} />
          </button>

          {/* Separator */}
          <div
            style={{
              width: "1px",
              height: "24px",
              background: "#e2e8f0",
              margin: "0 4px",
            }}
          />

          {/* To block */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              shiftTo(-1);
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "2px 4px",
              color: "#94a3b8",
            }}
          >
            <FaChevronLeft style={{ fontSize: "10px" }} />
          </button>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flex: 1,
            }}
          >
            <FaCalendarAlt
              style={{ color: "#0095da", fontSize: "13px", flexShrink: 0 }}
            />
            <span
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#0f172a",
                whiteSpace: "nowrap",
              }}
            >
              {fmt(internalTo)}
            </span>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              shiftTo(1);
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "2px 4px",
              color: "#94a3b8",
            }}
          >
            <FaChevronRight style={{ fontSize: "10px" }} />
          </button>
        </div>

        {/* Dropdown calendar */}
        {open &&
          ReactDOM.createPortal(
            <>
              {/* Backdrop */}
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,0.6)",
                  backdropFilter: "blur(4px)",
                  zIndex: 99998,
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                }}
              />

              {/* Modal */}
              <div
                style={{
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 99999,
                  background: "#0f172a",
                  borderRadius: "16px",
                  padding: "24px",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
                  width: isMobile ? "calc(100vw - 32px)" : "700px",
                  maxWidth: "95vw",
                  maxHeight: "90vh",
                  overflowY: "auto",
                }}
                onMouseDown={(e) => e.stopPropagation()} // 👈 key fix
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        background: "rgba(0,149,218,0.2)",
                        borderRadius: "10px",
                        padding: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FaCalendarAlt
                        style={{ color: "#0095da", fontSize: "16px" }}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "16px",
                        }}
                      >
                        Select Date Range
                      </div>
                      <div
                        style={{
                          color: "#475569",
                          fontSize: "12px",
                          marginTop: "2px",
                        }}
                      >
                        Choose your from and to dates
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <button
                      onMouseDown={(e) => e.stopPropagation()}
                      // onClick={(e) => {
                      //   e.stopPropagation();
                      //   const from = new Date();
                      //   const to = new Date(
                      //     new Date().setDate(new Date().getDate() + 10),
                      //   );
                      //   setInternalFrom(from);
                      //   setInternalTo(to);
                      //   onChange?.(from, to);
                      // }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const from = getISTDate(); // ✅
                        const to = new Date(getISTDate());
                        to.setDate(to.getDate() + 10); // ✅
                        setInternalFrom(from);
                        setInternalTo(to);
                        onChange?.(from, to);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#94a3b8",
                        fontSize: "13px",
                        cursor: "pointer",
                      }}
                    >
                      Reset
                    </button>
                    <button
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpen(false);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#94a3b8",
                        cursor: "pointer",
                        fontSize: "18px",
                        lineHeight: 1,
                      }}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Range bar */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "#1e293b",
                    borderRadius: "12px",
                    padding: "14px 20px",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flex: 1,
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "#0d9488",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#64748b",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                      }}
                    >
                      FROM
                    </span>
                    <span
                      style={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: "#fff",
                        marginLeft: "4px",
                      }}
                    >
                      {fmt(internalFrom)}
                    </span>
                  </div>
                  <FaChevronRight
                    style={{ color: "#334155", fontSize: "12px" }}
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flex: 1,
                      justifyContent: "flex-end",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: internalTo ? "#fff" : "#475569",
                        marginRight: "4px",
                      }}
                    >
                      {internalTo ? fmt(internalTo) : "Pick end date"}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#64748b",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                      }}
                    >
                      TO
                    </span>
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "#0d9488",
                        flexShrink: 0,
                      }}
                    />
                  </div>
                </div>

                {/* Calendar */}
                <div
                  className="dri-dp"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    overflowX: "auto",
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <DatePicker
                    selected={internalFrom}
                    onChange={handleChange}
                    startDate={internalFrom}
                    endDate={internalTo}
                    selectsRange
                    inline
                    monthsShown={isMobile ? 1 : 2}
                    // minDate={new Date()}
                    minDate={getISTDate()}
                  />
                </div>

                {/* Footer */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "20px",
                    paddingTop: "16px",
                    borderTop: "1px solid #1e293b",
                  }}
                >
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#0d9488",
                    }}
                  >
                    {internalFrom && internalTo
                      ? `${Math.round((internalTo - internalFrom) / 86400000)} days selected`
                      : ""}
                  </span>
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (internalFrom && internalTo) {
                        onChange?.(internalFrom, internalTo);
                        setOpen(false);
                      }
                    }}
                    disabled={!internalFrom || !internalTo}
                    style={{
                      background:
                        internalFrom && internalTo ? "#0d9488" : "#1e293b",
                      color: "white",
                      border: "none",
                      borderRadius: "12px",
                      padding: "10px 32px",
                      fontSize: "14px",
                      fontWeight: 700,
                      cursor:
                        internalFrom && internalTo ? "pointer" : "not-allowed",
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            </>,
            document.body,
          )}
      </div>
    </>
  );
}
